import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, switchMap } from 'rxjs';
import { environment } from '@env/environment';
import { AuthService } from '@app/services/auth/auth.service';
import {
  MembershipRequest,
  MembershipResponse,
  MembershipTokenResponse,
  MembershipType,
} from '@app/interfaces/membership.interface';

@Injectable({
  providedIn: 'root',
})
export class MembershipService {
  private readonly http = inject(HttpClient);
  private readonly authService = inject(AuthService);

  private readonly apiUrlMembership = environment.URL_MEMBRESIA;
  private readonly apiUrlMembershipToken = environment.URL_MEMBRESIA_TOKEN;
  private readonly defaultProduct: string = environment.P2P_PRODUCT ?? 'Arrendamientos';

  /**
   * Obtiene la URL para abrir el lightbox de Place to Pay.
   * @param sessionId ID de sesión enviado en header (fallback al AuthService interno).
   * @param recordId  quoteId / recordId asociado al pago.
   * @param type      'payment' | 'subscription'.
   * @param product   Producto (default: environment.P2P_PRODUCT).
   */
  getLightboxUrl(
    sessionId: string,
    recordId: string,
    type: MembershipType,
    product: string = this.defaultProduct,
  ): Observable<MembershipResponse> {
    if (sessionId) {
      this.authService.setSessionId(sessionId);
    }

    const payload: MembershipRequest = {
      product,
      input: {
        recordId,
        type,
        // El backend resuelve la IP real desde headers X-Forwarded-For.
        ipAddress: '0.0.0.0',
        userAgent: navigator.userAgent,
      },
    };

    return this.authService.createAuthHeaders().pipe(
      switchMap((headers) =>
        this.http.post<MembershipResponse>(this.apiUrlMembership, payload, { headers }),
      ),
    );
  }

  /**
   * Consulta el estado real de una transacción P2P (post-cierre del lightbox).
   */
  getTokenResponse(
    sessionId: string,
    recordId: string,
    product: string = this.defaultProduct,
  ): Observable<MembershipTokenResponse> {
    if (sessionId) {
      this.authService.setSessionId(sessionId);
    }

    const requestBody = {
      product,
      input: { recordId },
    };

    return this.authService.createAuthHeaders().pipe(
      switchMap((headers) =>
        this.http.post<MembershipTokenResponse>(this.apiUrlMembershipToken, requestBody, {
          headers,
        }),
      ),
    );
  }
}
